import { useEffect, useState } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const labels = { delivery_pending: "Respuesta pendiente", processing_v2: "En proceso", pending: "Pendientes", processing: "En proceso", completed: "Completados", rejected: "Rechazados", failed: "Fallidos", needs_review: "Por revisar", resolved: "Resueltos" };

export default function AdminWhatsAppQueue() {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/whatsapp-queue");
        if (!cancelled) { setSnapshot(data); setError(""); }
      } catch {
        if (!cancelled) setError("No se pudo actualizar la cola. Los datos anteriores pueden estar desactualizados.");
      } finally {
        if (!cancelled) timer = setTimeout(load, 15000);
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [revision]);

  const inspect = async (id) => {
    try {
      const { data } = await api.get(`/admin/whatsapp-queue/${encodeURIComponent(id)}`);
      setSelected(data); setNote("");
    } catch { setError("No se pudo abrir el mensaje para revisión."); }
  };
  const recover = async (action) => {
    setSaving(true);
    try {
      await api.post(`/admin/whatsapp-queue/${encodeURIComponent(selected._id)}/recovery`, { action, note });
      setSelected(null); setRevision((value) => value + 1);
    } catch (err) { setError(err.response?.data?.detail || "No se pudo aplicar la recuperación."); }
    finally { setSaving(false); }
  };
  const canRetry = selected?.phase === "delivery" && !(selected.responses || []).some((item) => ["sending", "unknown"].includes(item.status));

  return <Card>
    <CardHeader className="flex flex-row items-center justify-between gap-3">
      <CardTitle>Mensajes de WhatsApp</CardTitle>
      <Button variant="outline" onClick={() => setRevision((value) => value + 1)}>Actualizar</Button>
    </CardHeader>
    <CardContent className="space-y-5">
      {error && <p role="alert" className="text-red-600">{error}</p>}
      {!snapshot && !error && <p role="status">Cargando cola de mensajes…</p>}
      {snapshot && <>
        <p className="text-sm text-muted-foreground">Última lectura: {new Date(snapshot.checked_at).toLocaleTimeString("es-CO")}. Se actualiza cada 15 segundos mientras esta pestaña está abierta.</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[["Pendientes", (snapshot.counts.pending || 0) + (snapshot.counts.delivery_pending || 0)], ["En proceso", (snapshot.counts.processing || 0) + (snapshot.counts.processing_v2 || 0)], ["Por revisar", (snapshot.counts.needs_review || 0) + (snapshot.counts.failed || 0)], ["Consumidores activos", snapshot.active_consumers]].map(([label, value]) => <div key={label} className="rounded-lg border p-3"><p className="text-sm text-muted-foreground">{label}</p><p className="text-2xl font-semibold">{value}</p></div>)}
        </div>
        <p>Espera más antigua: {Math.ceil(snapshot.oldest_pending_seconds / 60)} min. Respuestas reportadas como fallidas por WhatsApp: {snapshot.provider_failed}.</p>
        {snapshot.alerts.map((alert) => <p role="alert" key={alert} className="rounded-lg border border-amber-400 p-3">{alert}</p>)}
        {snapshot.attention.length === 0 ? <p>No hay mensajes que requieran revisión.</p> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-left"><th className="p-2">Contacto</th><th className="p-2">Estado</th><th className="p-2">Recibido</th><th className="p-2">Acción</th></tr></thead><tbody>{snapshot.attention.map((job) => <tr key={job._id} className="border-b"><td className="p-2">{job.phone}</td><td className="p-2">{labels[job.status] || job.status}</td><td className="p-2">{new Date(job.created_at).toLocaleString("es-CO")}</td><td className="p-2"><Button variant="outline" onClick={() => inspect(job._id)}>Revisar</Button></td></tr>)}</tbody></table><p className="mt-2 text-sm text-muted-foreground">Se muestran hasta 50 casos, empezando por los más antiguos.</p></div>}
      </>}
      {selected && <div className="space-y-3 rounded-lg border p-4">
        <h3 className="font-semibold">Revisión de {selected.phone}</h3>
        <p className="break-all text-sm">Mensaje: {selected.message_id}</p>
        <p className="whitespace-pre-wrap">{selected.message?.text?.body || `Mensaje de tipo ${selected.msg_type}`}</p>
        <p className="text-sm">{selected.last_error || "WhatsApp reportó una entrega fallida."}</p>
        {(selected.linked_transactions || []).length > 0 && <div><h4 className="font-medium">Movimientos vinculados</h4><ul className="list-inside list-disc">{selected.linked_transactions.map((item) => <li key={item.id}>{item.currency} {item.amount} — {item.description}</li>)}</ul></div>}
        <p className="text-sm">Comprueba los movimientos del contacto antes de resolver el caso. Resolver deja constancia de la revisión y permite continuar la conversación; no crea ni modifica movimientos.</p>
        <label className="block text-sm" htmlFor="queue-review-note">Resultado de la revisión (mínimo 12 caracteres)</label>
        <textarea id="queue-review-note" className="w-full rounded border bg-background p-2" value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
        <div className="flex flex-wrap gap-2">
          {canRetry && <Button disabled={saving || note.trim().length < 12} onClick={() => recover("retry_delivery")}>Reintentar solo la respuesta</Button>}
          <Button disabled={saving || note.trim().length < 12} onClick={() => recover("resolve")}>Marcar como revisado y resuelto</Button>
          <Button variant="outline" disabled={saving} onClick={() => setSelected(null)}>Cerrar</Button>
        </div>
      </div>}
    </CardContent>
  </Card>;
}
